import React, { useState, useEffect } from 'react'
import GeoBlueLogo from '../assetsComponents/GeoBlueLogo'
import AllianceLogo from '../assetsComponents/AllianceLogo'
import WorldNomasLogo from '../assetsComponents/WorldNomasLogo'
import API from '../../utils/API'
import axios from 'axios'
import {
  useErrorService,
  useNotificationService,
  useUserService,
} from '@abroad/components'
import UnsavedDocumentModal from './UnsavedDocumentModal'
import FilePreview from './FilePreview'
import FileUploadZone from './FileUploadZone'
import UploadProgressBar from './UploadProgressBar'
import { validateFiles, isImageFile } from '../../utils/fileValidation'
import { gtmUploadExperiencePolicyAction } from './gtmQuestUploadFunctions'

const UploadPolicy = ({ questId }) => {
  const { user } = useUserService()
  const [uploadedPolicyDocs, setUploadedPolicyDocs] = useState([])
  const [isLoadingPage, setIsLoadingPage] = useState(true)
  const [policyData, setPolicyData] = useState()
  const [selectedFiles, setSelectedFiles] = useState([]) // [{ file, previewUrl, progress, status, fileNameFromSignedUrl }]
  const [dragActive, setDragActive] = useState(false)
  const { showError } = useErrorService()
  const Notification = useNotificationService()
  const userId = user?.id
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [pendingDeleteFile, setPendingDeleteFile] = useState('')

  // Fetch insurance data on mount
  useEffect(() => {
    const getQuestInsuranceData = async () => {
      if (userId && questId) {
        try {
          const { data } = await API.quests.getTravelDocument(questId, userId)
          setPolicyData(data)
          if (
            data &&
            data?.travelDocuments &&
            data?.travelDocuments?.length > 0
          ) {
            const insuranceDocs = data?.travelDocuments?.filter(
              (doc) => doc.documentType === 'insurance',
            )
            setUploadedPolicyDocs(insuranceDocs || [])
          } else {
            setUploadedPolicyDocs([])
          }
        } catch (error) {
          showError(error)
          setUploadedPolicyDocs([])
        } finally {
          setIsLoadingPage(false)
        }
      } else {
        setIsLoadingPage(false)
      }
    }
    if (user !== undefined) {
      getQuestInsuranceData()
    }
  }, [questId, userId])

  // Fetch preview URLs for all uploaded insurance files
  const [uploadedPreviews, setUploadedPreviews] = useState({}) // { fileName: url }
  useEffect(() => {
    const fetchAllPreviews = async () => {
      if (!uploadedPolicyDocs.length || !userId || !questId) return
      let previews = {}
      for (const doc of uploadedPolicyDocs) {
        for (const fileName of doc.fileName) {
          try {
            const { data } = await API.quests.downloadTravelDocument(
              questId,
              userId,
              fileName,
            )
            if (data && data.url) {
              previews[fileName] = data.url
            }
          } catch (e) {
            showError(e)
          }
        }
      }
      setUploadedPreviews(previews)
    }
    fetchAllPreviews()
  }, [uploadedPolicyDocs, userId, questId])

  // Handle file selection (by input or drag)
  const handleFiles = (files) => {
    const validation = validateFiles(files, {
      allowedTypes: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSizeInMB: 10,
    })

    const hasInvalidType = Array.from(files).some(
      (file) =>
        !['image/jpeg', 'image/png', 'application/pdf'].includes(file.type),
    )
    if (hasInvalidType) {
      Notification.showNotification('Please upload only JPG, PNG, or PDF files')
    } else if (validation.errors.length > 0) {
      Notification.showNotification(validation.errors.join(', '))
    }

    if (validation.validFiles.length > 0) {
      const newFileObjs = validation.validFiles.map((file) => ({
        file,
        previewUrl: isImageFile(file) ? URL.createObjectURL(file) : '',
        progress: 0,
        status: 'pending',
        fileNameFromSignedUrl: '',
      }))
      setSelectedFiles((prev) => [...prev, ...newFileObjs])
    }
  }

  // Start upload for new files
  useEffect(() => {
    selectedFiles.forEach((fileObj, idx) => {
      if (fileObj.status === 'pending') {
        uploadPolicyToSignedUrl(fileObj, idx)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles])

  // Upload logic for each file
  const uploadPolicyToSignedUrl = async (fileObj, idx) => {
    const file = fileObj.file
    if (!file) {
      Notification.showNotification('Please select a file to upload')
      return
    }
    setSelectedFiles((prev) =>
      prev.map((f, i) =>
        i === idx ? { ...f, status: 'uploading', progress: 0 } : f,
      ),
    )
    try {
      const contentType = file.name
        .substr(file.name.lastIndexOf('.') + 1)
        .toUpperCase()
      const documentType = 'insurance'
      const { data: signedUrlData } =
        await API.quests.getTravelDocumentSignedURL(
          questId,
          contentType,
          documentType,
        )
      if (signedUrlData && signedUrlData.url) {
        await axios.put(signedUrlData.url.url, file, {
          headers: {
            'Content-Type': signedUrlData.url.contentType,
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 95) / progressEvent.total,
              )
              setSelectedFiles((prev) =>
                prev.map((f, i) =>
                  i === idx ? { ...f, progress: percent } : f,
                ),
              )
            }
          },
        })
        // Finalize upload
        if (userId) {
          const { data: uploadApiResult } =
            await API.quests.uploadTravelDocument(questId, userId, {
              fileName: signedUrlData.fileName,
              documentType: 'insurance',
            })
          if (uploadApiResult && uploadApiResult?.travelDocuments) {
            const insuranceDocs = uploadApiResult?.travelDocuments?.filter(
              (doc) => doc.documentType === 'insurance',
            )
            setUploadedPolicyDocs(insuranceDocs || [])

            // Track GTM event for successful upload
            gtmUploadExperiencePolicyAction(
              questId,
              signedUrlData.fileName,
              file.type,
              userId,
            )
          }
          // Remove the file from selectedFiles after upload
          setSelectedFiles((prev) => prev.filter((_, i) => i !== idx))
        } else {
          throw new Error('User session error. Please try logging in again.')
        }
      } else {
        throw new Error('Could not get signed URL.')
      }
    } catch (e) {
      Notification.showNotification('Upload failed. Please try again.')
      setSelectedFiles((prev) =>
        prev.map((f, i) => (i === idx ? { ...f, status: 'error' } : f)),
      )
    }
  }

  // Remove file from selectedFiles
  const handleRemovePendingFile = (idx) => {
    setSelectedFiles((prev) => {
      // Revoke object URL to avoid memory leaks
      if (prev[idx]?.previewUrl) URL.revokeObjectURL(prev[idx].previewUrl)
      return prev.filter((_, i) => i !== idx)
    })
  }

  // Delete uploaded image
  const handleDelete = async (fileName) => {
    const documentType = 'insurance'
    try {
      await API.quests.deleteTravelDocument(
        questId,
        userId,
        fileName,
        documentType,
      )
      setUploadedPolicyDocs((currentDocs) => {
        const updatedDocs = currentDocs
          .map((doc) => ({
            ...doc,
            fileName: doc.fileName.filter((f) => f !== fileName),
          }))
          .filter((doc) => doc.fileName.length > 0)
        return updatedDocs
      })
    } catch (error) {
      showError(error)
    }
  }

  const handleDeleteIconClick = (fileName) => {
    setPendingDeleteFile(fileName)
    setShowDeleteModal(true)
  }
  const handleDeleteConfirm = async () => {
    if (pendingDeleteFile) {
      await handleDelete(pendingDeleteFile)
      setPendingDeleteFile('')
      setShowDeleteModal(false)
    }
  }
  const handleDeleteCancel = () => {
    setPendingDeleteFile('')
    setShowDeleteModal(false)
  }

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
    }
  }

  // Add handler to download and open PDF in new tab
  const handlePdfClick = async (fileName) => {
    if (!questId || !userId || !fileName) return
    try {
      const { data } = await API.quests.downloadTravelDocument(
        questId,
        userId,
        fileName,
      )
      if (data && data.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer')
      } else {
        showError({ message: 'Could not get download URL.' })
      }
    } catch (e) {
      showError(e)
    }
  }

  const processHtmlContent = (htmlContent) => {
    if (!htmlContent) return ''

    return htmlContent
  }

  return (
    <div>
      {isLoadingPage ? (
        <div className='text-center mt-40px'>
          <div className='spinner-border text-saffron-700' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <div className='d-flex justify-content-center align-items-center mt-4'>
            {policyData?.stepConfiguration?.step3?.subStep2?.description && (
              <div
                className='quest-rich-text text-start mb-3'
                dangerouslySetInnerHTML={{
                  __html: processHtmlContent(
                    policyData.stepConfiguration.step3.subStep2.description,
                  ),
                }}
              />
            )}
          </div>
          <div className='row'>
            <div className='col-md-4 col-sm-6 col-12 mb-4 mb-md-0'>
              <a
                href='https://bcbsglobalsolutions.com/individuals-and-families/international-medical-insurance/'
                target='_blank'
                rel='noopener noreferrer'
                className='p-4 h-100 border border-radius-10 w-100 d-flex justify-content-center align-items-center text-decoration-none'>
                <GeoBlueLogo />
              </a>
            </div>
            <div className='col-md-4 col-sm-6 col-12 mb-4 mb-md-0'>
              <a
                href='https://www.allianztravelinsurance.com/'
                target='_blank'
                rel='noopener noreferrer'
                className='p-4 h-100 border border-radius-10 w-100 d-flex justify-content-center align-items-center text-decoration-none'>
                <AllianceLogo />
              </a>
            </div>
            <div className='col-md-4 col-sm-6 col-12 mb-4 mb-md-0'>
              <a
                href='https://www.worldnomads.com/row/travel-insurance'
                target='_blank'
                rel='noopener noreferrer'
                className='p-4 h-100 border border-radius-10 w-100 d-flex justify-content-center align-items-center text-decoration-none'>
                <WorldNomasLogo />
              </a>
            </div>
          </div>

          {/* Unified grid for uploaded and uploading files */}
          <div className='row mt-5 mb-3'>
            {/* Build a unified list of slots: uploading first (oldest first), then uploaded (oldest first) */}
            {[
              // Uploaded files (from server), sorted by timestamp in file name (oldest first)
              ...uploadedPolicyDocs
                .flatMap((doc) =>
                  doc.fileName.map((fileName) => ({ fileName, doc })),
                )
                .sort((a, b) => {
                  // Extract timestamp from file name: insurance-<timestamp>.<ext>
                  const getTs = (f) => {
                    const match = f.fileName.match(/insurance-(\d+)\./)
                    return match ? parseInt(match[1], 10) : 0
                  }
                  return getTs(a) - getTs(b)
                })
                .map((item, idx) => ({
                  type: 'uploaded',
                  fileName: item.fileName,
                  doc: item.doc,
                  idx,
                })),
              // Uploading files (pending, uploading, or error) - oldest first
              ...selectedFiles.map((fileObj, idx) => ({
                type: 'uploading',
                fileObj,
                idx,
              })),
            ].map((slot, i) => {
              if (slot.type === 'uploading') {
                const { fileObj, idx } = slot
                return (
                  <div
                    key={'uploading-' + fileObj.file.name + idx}
                    className='col-xl-3 col-lg-4 col-md-4 col-12 mb-3'>
                    <div>
                      <FilePreview
                        file={fileObj.file}
                        previewUrl={fileObj.previewUrl}
                        onDelete={() => handleRemovePendingFile(idx)}
                        showDeleteButton={true}
                        className='position-relative'
                      />
                      {/* Progress bar while uploading */}
                      {(fileObj.status === 'uploading' ||
                        fileObj.status === 'pending') && (
                        <UploadProgressBar progress={fileObj.progress} />
                      )}
                      {/* Error state */}
                      {fileObj.status === 'error' && (
                        <div className='text-danger mt-2'>Failed</div>
                      )}
                    </div>
                  </div>
                )
              } else if (slot.type === 'uploaded') {
                const { fileName } = slot
                return (
                  <div
                    key={'uploaded-' + fileName}
                    className='col-xl-3 col-lg-4 col-md-4 col-12 mb-3'>
                    <FilePreview
                      fileName={fileName}
                      uploadedPreviewUrl={uploadedPreviews[fileName]}
                      onDelete={() => handleDeleteIconClick(fileName)}
                      onView={() => handlePdfClick(fileName)}
                      showDeleteButton={true}
                    />
                  </div>
                )
              } else {
                return null
              }
            })}
          </div>

          <FileUploadZone
            onFilesSelected={handleFiles}
            accept='image/jpeg,image/png,application/pdf'
            multiple={true}
            disabled={selectedFiles.some((f) => f.status === 'uploading')}
            dragActive={dragActive}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            label='Upload Policy'
            helperText='Supported Format: PDF, JPG, PNG'
          />

          {/* Delete confirmation modal */}
          <UnsavedDocumentModal
            showModal={showDeleteModal}
            handleClose={handleDeleteCancel}
            handleLeave={handleDeleteConfirm}
            modalTitle='Delete File'
            modalBody='Are you sure you want to delete this file? This action cannot be undone.'
            cancelLabel='Cancel'
            confirmLabel='Delete'
          />
        </>
      )}
    </div>
  )
}

export default UploadPolicy
